import { GQLStarTilesFragment } from '@theprintbar/graphql';
import { cn } from '@theprintbar/shared';
import {
	ButtonSize,
	CarouselNextButton,
	CarouselPreviousButton,
	Container,
	CustomSVG,
	OutlineLinkButton,
	PrimaryLinkButton,
	SmallArrowIcon,
} from '@theprintbar/ui';
import React from 'react';

export type TitleSectionContent = GQLStarTilesFragment['starTilesContainer'];

export interface TitleSectionProps {
	content: TitleSectionContent;
	carouselKey?: string;
	children?: React.ReactNode;
	fullWidthChild?: boolean;
	className?: string;
	buttonIcon?: CustomSVG;
	buttonSize?: ButtonSize;
	buttonClass?: string;
	isButtonStacked?: boolean;
	iconClass?: string;
	titleClass?: string;
	containerClass?: string;
	removeSpacing?: boolean;
}

export function TitleSection({
	content,
	carouselKey,
	children,
	fullWidthChild,
	className,
	buttonIcon = SmallArrowIcon,
	buttonSize,
	buttonClass,
	isButtonStacked,
	iconClass,
	titleClass,
	containerClass,
	removeSpacing,
	...props
}: TitleSectionProps) {
	const data = content.structure;

	const renderLinkButton = (extraClass: string) => {
		if (!data?.link) return null;
		const ButtonComponent = data.isPrimary ? PrimaryLinkButton : OutlineLinkButton;
		return (
			<ButtonComponent
				to={data.link || '#'}
				icon={buttonIcon}
				iconClass={cn('h-2.5 w-2.5', iconClass)}
				className={cn('font-belfast font-bold uppercase text-xs whitespace-nowrap h-fit', buttonClass, extraClass)}
				size={buttonSize}
			>
				{data.linkText}
			</ButtonComponent>
		);
	};

	return (
		<section className={cn('py-6 lg:py-8 lg:my-12', removeSpacing ? 'py-0 lg:py-0 lg:my-0' : '', className)} {...props}>
			<Container className={containerClass}>
				{(!!data?.title || !!data?.text || !!data?.link) && (
					<div className="flex items-start justify-between">
						<div className="mb-4 lg:mb-8">
							{!!data.title && (
								<h2 className={cn('text-2xl lg:text-[32px] font-light normal-case font-display leading-7', titleClass)}>
									{data.title}
								</h2>
							)}
							{data.text && <p className="text-sm">{data.text}</p>}
							{data.link && buttonSize !== ButtonSize.L && isButtonStacked && renderLinkButton('md:hidden')}
						</div>
						<div className="flex items-center justify-end flex-row space-x-7">
							{data.link && renderLinkButton((ButtonSize.L || !isButtonStacked) && 'hidden md:inline-flex')}
							{!!carouselKey && (
								<div className="hidden md:block space-x-7">
									<CarouselPreviousButton carouselKey={carouselKey} />
									<CarouselNextButton carouselKey={carouselKey} />
								</div>
							)}
						</div>
					</div>
				)}
				{!fullWidthChild && children}
			</Container>
			{fullWidthChild && children}
			{data?.link && buttonSize === ButtonSize.L && <div className="px-4">{renderLinkButton('md:hidden w-full')}</div>}
		</section>
	);
}
